import { useContext, useCallback, useState, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { UseModalProps } from '@trinity/chat-components/components/Modal/index.js';
import { Modal, Header, useModal } from '@trinity/chat-components/components/Modal/index.js';
import { Button } from '@trinity/chat-components/components/presentation/Button/index.js';
import { Radio } from '@trinity/chat-components/components/presentation/Radio.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { SelectedTermsContext, TranscriptReplacementsContext, ProjectTermsContext } from '@/containers/Project.Terms';
import { TransformedProjectTermsContext } from './Context';
import styles from './style/MergeTermsModal.module.css';

export const MergeTermsModal = (props: UseModalProps) => {
  const { selectedTermValues, clearTermValues } = useContext(SelectedTermsContext);
  const { projectId } = useContext(TranscriptReplacementsContext);
  const { query: termsQuery } = useContext(ProjectTermsContext);
  const { terms } = useContext(TransformedProjectTermsContext);
  const [selectedTerm, setSelectedTerm] = useState('');

  const apiClient = useChatApiClient();

  const mergeableTerms = useMemo(() => {
    return terms.filter(t => selectedTermValues.includes(t.termValue));
  }, [selectedTermValues, terms]);

  const mergeMutation = useMutation({
    mutationKey: ['post:projects/entities/merge', projectId],
    mutationFn: () => {
      const selectedTermData = terms.find(t => t.termValue === selectedTerm);
      const sourceEntities = mergeableTerms
        .filter(t => t.termValue !== selectedTerm)
        .map(t => ({
          name: t.termValue,
          type: t.entities?.length > 0 ? t.entities[0].type : 'PERSON',
        }));

      const targetEntity = {
        name: selectedTerm,
        type: selectedTermData?.entities?.length > 0 ? selectedTermData.entities[0].type : 'PERSON',
      };

      return apiClient.projectsEntities.mergeEntities({
        projectId,
        sourceEntities,
        targetEntity,
      });
    },
    onSuccess: () => {
      termsQuery.refetch();
      clearTermValues();
      props.onClose();
    },
  });

  const onMerge = useCallback(() => {
    mergeMutation.mutate();
  }, [mergeMutation]);
  return (
    <Modal {...props}>
      <Header>Choose Term to Merge Into</Header>
      <div>
        {mergeableTerms.map(t => (
          <div key={t.termValue} className={styles.termRow}>
            <Radio checked={t.termValue === selectedTerm} onClick={() => setSelectedTerm(t.termValue)} />
            {t.termValue}
          </div>
        ))}
      </div>
      <div className={styles.buttons}>
        <Button
          variant='brick'
          color='destructive'
          onClick={props.onClose}
          disabled={mergeMutation.isPending}>Cancel
        </Button>
        <Button
          variant='brick'
          onClick={onMerge}
          disabled={!selectedTerm || mergeMutation.isPending}>
          {mergeMutation.isPending ? 'Merging...' : 'Merge'}
        </Button>
      </div>
    </Modal>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useMergeTermsModal = () => useModal(MergeTermsModal);